import React from 'react';
import { Copy, Check, Edit3, Save, X, RefreshCw } from 'lucide-react';
import PhotoStatusBadge from './PhotoStatusBadge';
import { usePhotoMetadataContext } from '../../contexts/PhotoMetadataContext';

const PhotoMetadataFields = ({
  result,
  isExpanded,
  onToggleExpand,
  isApproved
}) => {
  const {
    editingField,
    editingValue,
    setEditingValue,
    savingState,
    copiedStates,
    removingTags,
    startEditing,
    cancelEditing,
    saveEdit,
    handleKeyPress,
    copyToClipboard,
    removeTag
  } = usePhotoMetadataContext();

  const copyKey = (field) => `${result.photoId}-${field}`;
  const isEditingField = (field) =>
    editingField?.photoId === result.photoId && editingField?.field === field;

  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-start justify-between mb-1">
        <div className="flex-1 min-w-0 pr-3">
          {result.status === 'processed' ? (
            isEditingField('title') ? (
              <div className="flex items-center space-x-2 mb-2">
                <input
                  type="text"
                  value={editingValue}
                  onChange={(event) => setEditingValue(event.target.value)}
                  onKeyDown={handleKeyPress}
                  className="flex-1 text-xl font-bold text-gray-900 bg-white border border-blue-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                  placeholder="Enter title..."
                />
                <button
                  onClick={saveEdit}
                  disabled={savingState[copyKey('title')]}
                  className="text-green-600 hover:text-green-800 disabled:opacity-50"
                >
                  <Save className="h-4 w-4" />
                </button>
                <button
                  onClick={cancelEditing}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="group mb-1 flex items-start justify-between">
                <h3
                  className="text-xl font-bold text-gray-900 cursor-pointer hover:text-blue-600 transition-colors flex-1 pr-2"
                  onClick={() => startEditing(result.photoId, 'title', result.generatedTitle)}
                  title="Click to edit"
                >
                  {result.generatedTitle || 'No title generated'}
                </h3>
                <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  <button
                    onClick={() => startEditing(result.photoId, 'title', result.generatedTitle)}
                    className="text-gray-400 hover:text-gray-600 text-xs flex items-center space-x-1"
                    title="Edit title"
                  >
                    <Edit3 className="h-3 w-3" />
                    <span>Edit</span>
                  </button>
                  {result.generatedTitle && (
                    <button
                      onClick={() => copyToClipboard(result.generatedTitle, 'title', result.photoId)}
                      className="text-gray-400 hover:text-gray-600 text-xs flex items-center space-x-1"
                      title="Copy title"
                    >
                      {copiedStates[copyKey('title')] ? (
                        <>
                          <Check className="h-3 w-3 text-green-600" />
                          <span className="text-green-600">Copied</span>
                        </>
                      ) : (
                        <>
                          <Copy className="h-3 w-3" />
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            )
          ) : (
            <h3 className="text-xl font-bold text-gray-700 mb-1">
              {result.originalTitle || 'Untitled Photo'}
            </h3>
          )}
        </div>
        <PhotoStatusBadge result={result} isApproved={isApproved} />
      </div>

      {result.status === 'processed' ? (
        <div className="mb-2">
          {isEditingField('description') ? (
            <div className="space-y-2">
              <textarea
                value={editingValue}
                onChange={(event) => setEditingValue(event.target.value)}
                onKeyDown={handleKeyPress}
                className="w-full text-sm text-gray-700 bg-white border border-blue-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 leading-relaxed resize-vertical"
                rows={3}
                placeholder="Enter description..."
              />
              <div className="flex items-center space-x-2">
                <button
                  onClick={saveEdit}
                  disabled={savingState[copyKey('description')]}
                  className="text-green-600 hover:text-green-800 disabled:opacity-50 flex items-center space-x-1"
                >
                  <Save className="h-3 w-3" />
                  <span className="text-xs">Save</span>
                </button>
                <button
                  onClick={cancelEditing}
                  className="text-gray-400 hover:text-gray-600 flex items-center space-x-1"
                >
                  <X className="h-3 w-3" />
                  <span className="text-xs">Cancel</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="group">
              <p
                className="text-sm text-gray-600 leading-relaxed cursor-pointer hover:text-blue-600 transition-colors"
                onClick={() => startEditing(result.photoId, 'description', result.generatedDescription)}
                title="Click to edit"
              >
                {result.generatedDescription || (
                  <span className="italic text-gray-400">No description generated</span>
                )}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="mb-2">
          <p className="text-sm text-gray-500 italic">
            {result.status === 'pending'
              ? 'Waiting to process...'
              : result.status === 'retry_pending'
              ? 'Retrying...'
              : result.status === 'failed'
              ? 'Processing failed'
              : result.status === 'skipped'
              ? 'Skipped'
              : 'Processing...'}
          </p>
        </div>
      )}

      {result.status === 'processed' && result.generatedTags?.length > 0 && (
        <div className="mb-2">
          <div className="flex flex-wrap gap-1">
            {result.generatedTags.map((tag, index) => {
              const isLenscribe = tag.toLowerCase() === 'lenscribe';
              const tagKey = `${result.photoId}-${tag}`;
              const isRemoving = removingTags?.has(tagKey);
              const copyStateKey = copyKey(`tag-${index}`);

              return (
                <span
                  key={index}
                  className={`group relative inline-flex items-center px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full transition-all duration-200 hover:bg-gray-200 ${
                    isRemoving ? 'opacity-50 pointer-events-none' : ''
                  }`}
                >
                  <span
                    className="cursor-pointer"
                    title="Click to copy"
                    onClick={() =>
                      !isRemoving && copyToClipboard(tag, `tag-${index}`, result.photoId)
                    }
                  >
                    {tag}
                  </span>
                  {copiedStates[copyStateKey] && !isRemoving && (
                    <Check className="ml-1 h-3 w-3 text-green-600" />
                  )}
                  {!isLenscribe && (
                    <button
                      onClick={(event) => {
                        event.stopPropagation();
                        if (!isRemoving) {
                          removeTag(result.photoId, tag);
                        }
                      }}
                      disabled={isRemoving}
                      className={`ml-1 transition-all duration-150 ${
                        isRemoving
                          ? 'opacity-100'
                          : 'opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600'
                      }`}
                      title={isRemoving ? 'Removing...' : 'Remove tag'}
                    >
                      {isRemoving ? (
                        <RefreshCw className="h-3 w-3 animate-spin text-gray-500" />
                      ) : (
                        <X className="h-3 w-3" />
                      )}
                    </button>
                  )}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {result.status === 'processed' && !result.applied && (result.originalTitle || result.originalDescription || result.originalTags?.length > 0) && (
        <div className="mb-1 p-2 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start space-x-2">
            <svg
              className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-amber-800 mb-1.5">Replacing existing content • Original backed up for undo</p>
              <div className="space-y-1.5">
                {result.originalTitle && (
                  <div>
                    <span className="text-xs text-amber-700 font-medium">Current: </span>
                    <span className="text-xs text-amber-900 line-clamp-1">{result.originalTitle}</span>
                  </div>
                )}
                {result.originalDescription && (
                  <div>
                    <span className="text-xs text-amber-700 font-medium">Current: </span>
                    <span className="text-xs text-amber-900 line-clamp-2">{result.originalDescription}</span>
                  </div>
                )}
                {result.originalTags?.length > 0 && (
                  <div>
                    <span className="text-xs text-amber-700 font-medium">Current tags: </span>
                    <span className="text-xs text-amber-900">{result.originalTags.join(', ')}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoMetadataFields;
